import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { config } from '../../constants/config';
import {
  Segment, Header, Dropdown, Input, Button, Icon, Message, Loader,
  Label, Statistic, List, Grid, Confirm,
} from 'semantic-ui-react';
import { apiClient } from '../../apiTools/apiClient';

/**
 * IngestPreviewPanel — the BC's "Ingest Lab": layout-aware ingest confirmation gate (Phase 5).
 *
 * Layout-aware ingest converts a book's source PDF to HTML (position + font preserved), proposes a
 * per-LitGroup PROFILE, and CLASSIFIES every paragraph (body / heading / scene break / footnote /
 * sidenote / dropped chrome / trimmed front-&-rear matter). This panel shows that read, color-coded on
 * an annotated page canvas, AND lets the BC spot-correct it (brick 4b): click a box to re-tag its kind
 * or set it as the body start/end boundary. Corrections apply live (client-side, no re-conversion) and
 * are saved as an override the phase-6 ingest reproduces — so what the BC confirms is what gets imported.
 *
 * The preview converts the PDF to HTML through ConvertAPI, which is billed — but the result is now
 * cached on the source PDF's content hash, so a given manuscript converts ONCE and every later preview,
 * import and re-ingest is a free cache hit. The run used to sit behind a confirm for that cost; with
 * the cache in place the modal was guarding a free, read-only action, so it is gone. A page range still
 * keeps the first conversion of a new PDF cheap.
 *
 * @param {string} litGroupUuid
 * @param {Array<{uuid,title,sourceFileName}>} books  the group's books (from the casting payload)
 */

const KIND_STYLE = {
  body: { color: '#2185d0', label: 'Body', kept: true },
  heading: { color: '#6435c9', label: 'Heading', kept: true },
  scenebreak: { color: '#00b5ad', label: 'Scene break', kept: true },
  footnote: { color: '#f2711c', label: 'Footnote', kept: true },
  sidenote: { color: '#fbbd08', label: 'Sidenote', kept: true },
  header: { color: '#9aa0a6', label: 'Running header', kept: false },
  pagenum: { color: '#c4c8cc', label: 'Page number', kept: false },
  trimmed: { color: '#d9534f', label: 'Trimmed (front/rear matter)', kept: false, dashed: true },
};
const kindStyle = (k) => KIND_STYLE[k] || { color: '#888', label: k, kept: false };

// The kinds a BC can assign when re-tagging an element. 'drop' → excluded (rendered as trimmed).
const RETAG = [
  { kind: 'body', label: 'Body' }, { kind: 'heading', label: 'Heading' }, { kind: 'scenebreak', label: 'Scene break' },
  { kind: 'footnote', label: 'Footnote' }, { kind: 'sidenote', label: 'Sidenote' }, { kind: 'drop', label: 'Drop' },
];

// Reduce the working override state to the persisted shape (or null if nothing set).
const cleanOverrides = (ov) => {
  if (!ov) return null;
  const out = {};
  if (ov.bodyStartIdx != null) out.bodyStartIdx = ov.bodyStartIdx;
  if (ov.bodyEndIdx != null) out.bodyEndIdx = ov.bodyEndIdx;
  const kinds = {};
  Object.entries(ov.kinds || {}).forEach(([k, v]) => { if (v) kinds[k] = v; });
  if (Object.keys(kinds).length) out.kinds = kinds;
  return Object.keys(out).length ? out : null;
};
const EMPTY_OV = { kinds: {}, bodyStartIdx: null, bodyEndIdx: null };

const ElementBox = ({ el, kind, pageW, pageH, selected, onSelect }) => {
  const s = kindStyle(kind);
  const pct = (v, whole) => `${whole ? (v / whole) * 100 : 0}%`;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onSelect(el.idx); }}
      title={`[${s.label}]${el.italic ? ' · italic' : ''} ${el.font || ''}\n${el.text || ''}`}
      style={{
        position: 'absolute',
        left: pct(el.left, pageW), top: pct(el.top, pageH),
        width: pct(Math.max(el.width, 0.6), pageW), height: pct(Math.max(el.height, 0.9), pageH),
        minHeight: 2, boxSizing: 'border-box',
        background: selected ? `${s.color}66` : `${s.color}22`,
        border: `${selected ? 2 : 1}px ${s.dashed ? 'dashed' : 'solid'} ${s.color}`,
        boxShadow: selected ? '0 0 0 2px rgba(0,0,0,0.35)' : 'none',
        borderRadius: 2, overflow: 'hidden', fontSize: 4, lineHeight: '4px',
        color: s.color, padding: '0 1px', cursor: 'pointer',
      }}
    >
      {el.text ? el.text.slice(0, 40) : ''}
    </div>
  );
};

const PageCanvas = ({ page, effectiveKind, selectedIdx, onSelect }) => {
  if (!page) return null;
  const { width, height, elements } = page;
  const RENDER_W = 340;
  const aspect = width ? height / width : 1.3;
  return (
    <div
      onClick={() => onSelect(null)}
      style={{
        position: 'relative', width: RENDER_W, height: RENDER_W * aspect,
        background: '#fff', border: '1px solid #d4d4d5', borderRadius: 4,
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)', margin: '0 auto',
      }}
    >
      {elements.map((el) => (
        <ElementBox key={el.idx} el={el} kind={effectiveKind(el)} pageW={width} pageH={height}
          selected={el.idx === selectedIdx} onSelect={onSelect} />
      ))}
    </div>
  );
};

const Legend = ({ kindsPresent }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35em 1em', margin: '0.5em 0' }}>
    {Object.entries(KIND_STYLE).filter(([k]) => kindsPresent.has(k)).map(([k, s]) => (
      <span key={k} style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.82em', color: '#555' }}>
        <span style={{
          display: 'inline-block', width: 12, height: 12, marginRight: 5, borderRadius: 2,
          background: `${s.color}22`, border: `1px ${s.dashed ? 'dashed' : 'solid'} ${s.color}`,
        }} />
        {s.label}{s.kept ? '' : ' · dropped'}
      </span>
    ))}
  </div>
);

const ProfileCard = ({ profile }) => {
  if (!profile) return null;
  const chip = (label, value, color) => (
    <Label style={{ marginBottom: 4 }} color={color}>{label}<Label.Detail>{value}</Label.Detail></Label>
  );
  const marker = profile.footnoteMarker || 'none';
  return (
    <Segment>
      <Header as="h5" style={{ marginBottom: '0.5em' }}><Icon name="clipboard outline" /> Proposed profile</Header>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4em' }}>
        {chip('Body font', profile.bodyFont || '?', 'blue')}
        {chip('Mirror margins', profile.mirrorMargins ? 'yes' : 'no', profile.mirrorMargins ? 'teal' : undefined)}
        {chip('“•••” means', profile.sceneBreakBullets ? 'scene break' : 'footnote leader', profile.sceneBreakBullets ? 'teal' : 'orange')}
        {chip('Footnote marker', marker, marker === 'none' ? undefined : 'orange')}
      </div>
      {profile.fontRoles && Object.keys(profile.fontRoles).length > 0 && (
        <div style={{ marginTop: '0.6em' }}>
          <span style={{ fontSize: '0.85em', color: '#888', marginRight: '0.5em' }}>Font roles:</span>
          {Object.entries(profile.fontRoles).map(([font, role]) => (
            <Label key={font} basic size="tiny" style={{ marginBottom: 3 }}>
              {font === '?' ? '(unknown)' : font}<Label.Detail>{role}</Label.Detail>
            </Label>
          ))}
        </div>
      )}
    </Segment>
  );
};

const IngestPreviewPanel = ({ litGroupUuid, books = [], engineVersion = null }) => {
  const withPdf = useMemo(() => books.filter((b) => b.sourceFileName), [books]);
  const [bookUuid, setBookUuid] = useState(withPdf.length === 1 ? withPdf[0].uuid : '');
  const [pageRange, setPageRange] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [pageIdx, setPageIdx] = useState(0);
  // Spot-correct (brick 4b): working overrides + selection + save state.
  const [overrides, setOverrides] = useState(EMPTY_OV);
  const [savedOverrides, setSavedOverrides] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  // Re-ingest: apply the SAVED overrides to the actual imported book.
  const [reingesting, setReingesting] = useState(false);
  const [reingestConfirm, setReingestConfirm] = useState(false);
  const [reingestMsg, setReingestMsg] = useState(null);
  // Live re-ingest status via SSE (mirrors TalentAgencyPage's casting stream) so
  // the button stays disabled until the BACKGROUND job actually finishes — the
  // POST only returns a 202 in milliseconds.
  const userState = useSelector((s) => s.user);
  const userData = userState?.data || userState?.profile?.data || userState || {};
  const userId = userData.userId || userData.id;
  const reingestEsRef = useRef(null);
  useEffect(() => () => {
    if (reingestEsRef.current) { try { reingestEsRef.current.close(); } catch (_) {} reingestEsRef.current = null; }
  }, []);

  const bookOptions = withPdf.map((b) => ({ key: b.uuid, value: b.uuid, text: b.title }));

  const runPreview = async () => {
    if (!bookUuid) { setError('Pick a book first.'); return; }
    setLoading(true); setError(null); setPreview(null); setPageIdx(0); setSelectedIdx(null); setSaveMsg(null);
    try {
      const body = {};
      const pr = pageRange.trim();
      if (pr) body.pageRange = pr;
      // PDF→HTML is an external round-trip that easily exceeds the 30s default on a full book.
      const r = await apiClient.post(
        `/litgroups/${litGroupUuid}/books/${bookUuid}/ingest-preview`, body, {}, { timeout: 180000 });
      setPreview(r.data);
      const saved = r.data.savedOverrides || null;
      setSavedOverrides(saved);
      setOverrides({ kinds: (saved && saved.kinds) || {}, bodyStartIdx: (saved && saved.bodyStartIdx) ?? null, bodyEndIdx: (saved && saved.bodyEndIdx) ?? null });
      const firstBody = (r.data.pages || []).findIndex((pg) => pg.elements.some((e) => e.kind === 'body'));
      setPageIdx(firstBody >= 0 ? firstBody : 0);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Preview failed');
    } finally {
      setLoading(false);
    }
  };

  // Effective kind = boundary override (outside [start,end] → trimmed) then per-element re-tag, else the
  // classifier's kind. Mirrors the server's applyKinds/resolveTrim so the preview == the phase-6 import.
  const effectiveKind = useCallback((el) => {
    const { bodyStartIdx, bodyEndIdx, kinds } = overrides;
    if (bodyStartIdx != null && el.idx < bodyStartIdx) return 'trimmed';
    if (bodyEndIdx != null && el.idx > bodyEndIdx) return 'trimmed';
    const k = kinds[el.idx];
    if (k) return k === 'drop' ? 'trimmed' : k;
    return el.kind;
  }, [overrides]);

  const pages = preview?.pages || [];
  const page = pages[pageIdx];
  const allElements = useMemo(() => (preview?.pages || []).flatMap((pg) => pg.elements), [preview]);
  const kindsPresent = useMemo(() => {
    const set = new Set();
    allElements.forEach((e) => set.add(effectiveKind(e)));
    return set;
  }, [allElements, effectiveKind]);

  // Live summary + note list, recomputed from effective kinds (client-side — no re-conversion).
  const derived = useMemo(() => {
    let bodyParas = 0, bodyChars = 0, scene = 0, header = 0, pagenum = 0, trimmed = 0;
    const noteEls = [];
    allElements.forEach((e) => {
      const k = effectiveKind(e);
      if (k === 'body' || k === 'heading') { bodyParas++; bodyChars += (e.text || '').length; }
      else if (k === 'scenebreak') scene++;
      else if (k === 'header') header++;
      else if (k === 'pagenum') pagenum++;
      else if (k === 'trimmed') trimmed++;
      else if (k === 'sidenote' || k === 'footnote') noteEls.push({ idx: e.idx, noteType: k, text: e.text || '', page: e.page, top: e.top });
    });
    // Group adjacent same-type note lines on a page into ONE blob — mirrors the server's assemble(), so the
    // list shows the real note count (~108) instead of every wrapped line as its own row. Each blob carries
    // all its member idxs so a re-tag / drop applies to the whole note, not just its first line.
    noteEls.sort((a, b) => a.page - b.page || a.top - b.top);
    const notes = [];
    noteEls.forEach((e) => {
      const last = notes[notes.length - 1];
      if (last && last.noteType === e.noteType && last.page === e.page && (e.top - last._top) < 4) {
        last.text += ' ' + e.text; last._top = e.top; last.idxs.push(e.idx);
      } else {
        notes.push({ idx: e.idx, idxs: [e.idx], noteType: e.noteType, text: e.text, page: e.page, _top: e.top });
      }
    });
    return { bodyParas, bodyChars, scene, chrome: header + pagenum, trimmed, notes };
  }, [allElements, effectiveKind]);

  const selectedEl = useMemo(() => allElements.find((e) => e.idx === selectedIdx) || null, [allElements, selectedIdx]);

  const dirty = useMemo(
    () => JSON.stringify(cleanOverrides(overrides)) !== JSON.stringify(savedOverrides || null),
    [overrides, savedOverrides]);
  const hasOverrides = useMemo(() => cleanOverrides(overrides) != null, [overrides]);   // anything to clear

  // Re-tag one or more elements. Re-tagging back to the classifier's original kind clears the override.
  const setKinds = (idxs, kind) => {
    setOverrides((o) => {
      const kinds = { ...o.kinds };
      idxs.forEach((id) => {
        const orig = allElements.find((e) => e.idx === id)?.kind;
        if (kind === orig) delete kinds[id]; else kinds[id] = kind;
      });
      return { ...o, kinds };
    });
  };
  const setKind = (idx, kind) => setKinds([idx], kind);
  // Jump the annotated page to a note blob's page and select its first line.
  const goToNotePage = (n) => {
    const pi = (preview?.pages || []).findIndex((pg) => pg.page === n.page);
    if (pi >= 0) { setPageIdx(pi); setSelectedIdx(n.idx); }
  };
  const setBoundary = (which, idx) => setOverrides((o) => ({ ...o, [which]: o[which] === idx ? null : idx }));
  // Revert = undo UNSAVED edits, back to the last saved confirmation. Reset all = clear EVERYTHING back to
  // the classifier's original read (then Save to persist the clear).
  const revertToSaved = () => {
    setOverrides({
      kinds: (savedOverrides && savedOverrides.kinds) || {},
      bodyStartIdx: (savedOverrides && savedOverrides.bodyStartIdx) ?? null,
      bodyEndIdx: (savedOverrides && savedOverrides.bodyEndIdx) ?? null,
    });
    setSelectedIdx(null);
  };
  const resetOverrides = () => { setOverrides({ kinds: {}, bodyStartIdx: null, bodyEndIdx: null }); setSelectedIdx(null); };

  const saveOverrides = async () => {
    setSaving(true); setSaveMsg(null); setError(null);
    try {
      const payload = cleanOverrides(overrides);
      const r = await apiClient.put(`/litgroups/${litGroupUuid}/books/${bookUuid}/ingest-overrides`, { overrides: payload });
      setSavedOverrides(r.data.overrides || null);
      setSaveMsg(payload ? 'Confirmation saved — the import will use these corrections.' : 'Corrections cleared.');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

// ---------------------------------------------------------------------------
  // Replace the source PDF
  // ---------------------------------------------------------------------------
  // ⭐ The manuscript a book was imported from is uploaded ONCE, in the prototype flow. Nothing
  // could put new bytes where the ingest reads them, so an edited manuscript could not reach a
  // published book — you re-exported from InDesign and had nowhere to put it.
  //
  // ⛔ THE FILENAME IS NOT OURS TO CHOOSE. The upload alias is derived from the book TITLE, the
  // local file has whatever name InDesign gave it, and the re-ingest resolves a third thing from
  // the graph. Picking wrong writes a SECOND object and the re-ingest silently re-reads the old
  // bytes. The server hands back both the name and a signed URL already bound to it.
  const [srcPdf, setSrcPdf] = useState(null);        // { fileName, bookName, stamped, current }
  const [srcFile, setSrcFile] = useState(null);      // the File the operator picked
  const [srcBusy, setSrcBusy] = useState(false);
  const [srcConfirm, setSrcConfirm] = useState(false);
  const [srcMsg, setSrcMsg] = useState(null);
  const srcInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!litGroupUuid || !bookUuid) { setSrcPdf(null); return undefined; }
    (async () => {
      try {
        const r = await apiClient.get(`/litgroups/${litGroupUuid}/books/${bookUuid}/source-pdf`);
        if (!cancelled) setSrcPdf(r.data);
      } catch (_) { if (!cancelled) setSrcPdf(null); }
    })();
    return () => { cancelled = true; };
  }, [litGroupUuid, bookUuid]);

  const runReplaceSourcePdf = async () => {
    setSrcConfirm(false); setSrcBusy(true); setSrcMsg(null); setError(null);
    try {
      // 1. Server backs up what is stored and signs a PUT bound to the resolved name.
      const start = await apiClient.post(`/litgroups/${litGroupUuid}/books/${bookUuid}/source-pdf/start`, {});
      const { signedUrl, fileName, backup } = start.data;

      // 2. The bytes go straight to S3. A manuscript is megabytes and has no business
      //    transiting the API.
      const put = await fetch(signedUrl, {
        method: 'PUT', body: srcFile, headers: { 'Content-Type': 'application/pdf' },
      });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);

      // 3. Server verifies the size, mirrors to the LitGroup copy, stamps sourceFileName.
      const done = await apiClient.post(`/litgroups/${litGroupUuid}/books/${bookUuid}/source-pdf/finish`,
        { size: srcFile.size });
      setSrcPdf(done.data);
      setSrcFile(null);
      if (srcInputRef.current) srcInputRef.current.value = '';
      setSrcMsg(`Replaced ${fileName} — ${done.data.current.size.toLocaleString()} bytes.`
        + (backup ? ` Previous version kept as ${backup}.` : '')
        + ' Re-ingest to read it.');
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not replace the source PDF');
    } finally {
      setSrcBusy(false);
    }
  };

  // Re-ingest applies the SAVED overrides to the imported book (regenerates the render script + lines). The
  // POST returns 202 immediately; the work runs in the background (a few minutes).
  const runReingest = async () => {
    setReingestConfirm(false); setReingesting(true); setReingestMsg(null); setError(null);

    // Subscribe to the job stream BEFORE firing, so we don't miss a fast finish.
    // The button stays disabled (reingesting=true) until COMPLETE/ERROR arrives.
    if (reingestEsRef.current) { try { reingestEsRef.current.close(); } catch (_) {} reingestEsRef.current = null; }
    if (userId) {
      try {
        const es = new EventSource(`${config.BASE_URL}/sse-endpoint?userId=${userId}`);
        reingestEsRef.current = es;
        es.onmessage = (event) => {
          try {
            const parsed = JSON.parse(event.data);
            const d = parsed.data || parsed;
            if (d.bookUuid && d.bookUuid !== bookUuid) return;
            if (d.type === 'REINGEST_PROGRESS') {
              setReingestMsg(d.message || 'Re-ingesting…');
            } else if (d.type === 'REINGEST_COMPLETE') {
              es.close(); reingestEsRef.current = null;
              setReingesting(false);
              setReingestMsg(d.message || 'Re-ingest complete.');
            } else if (d.type === 'REINGEST_ERROR') {
              es.close(); reingestEsRef.current = null;
              setReingesting(false);
              setError(d.message || 'Re-ingest failed');
            }
          } catch (_) { /* ignore non-JSON keepalives */ }
        };
        es.onerror = () => { /* non-fatal — the button re-enables on COMPLETE or reload */ };
      } catch (_) { /* EventSource unavailable — button re-enables on reload */ }
    }

    try {
      await apiClient.post(`/litgroups/${litGroupUuid}/books/${bookUuid}/re-ingest`, {});
      setReingestMsg('Re-ingesting in the background — the button will re-enable when it finishes.');
    } catch (e) {
      // Failed to even start → nothing is running, so re-enable immediately.
      if (reingestEsRef.current) { try { reingestEsRef.current.close(); } catch (_) {} reingestEsRef.current = null; }
      setReingesting(false);
      setError(e.response?.data?.error || e.message || 'Re-ingest failed to start');
    }
  };

  // (Selection persists across page flips so a blob click can navigate AND select; the canvas background
  // click clears it.)

  // ---------------------------------------------------------------------
  // Hyphen review — the tokens the deterministic rule could not prove.
  //
  // The overlay judges ~709 of these per book on self-reported confidence and nothing has
  // ever checked it: three imports of the same PDF disagreed on 13, several of them WRONG
  // joins on real compounds ("two-thirds" -> "twothirds"). The server surfaces only the
  // doubtful ones — never asked, below threshold, barely cleared, or kept while neither half
  // is a word — and only those that survive into the body. A decision here is stamped
  // operator-confirmed and is never re-asked or overturned by a later model run.
  // ---------------------------------------------------------------------
  const hyphenReview = preview?.hyphenReview || null;
  const [hyphenChoices, setHyphenChoices] = useState({});   // token -> 'join' | 'keep'
  const [savingHyphens, setSavingHyphens] = useState(false);
  const [hyphenMsg, setHyphenMsg] = useState(null);

  const chooseHyphen = (token, verdict) =>
    setHyphenChoices((prev) => (prev[token] === verdict
      ? Object.fromEntries(Object.entries(prev).filter(([k]) => k !== token))   // click again to unset
      : { ...prev, [token]: verdict }));

  // Moves rows between the work list and the decided list LOCALLY after a save. Re-running
  // the preview would be the obvious way to refresh, and it is the wrong one: `runPreview`
  // is a billed ConvertAPI conversion, so an undo would cost a PDF→HTML call.
  const applyHyphenResultLocally = (decisions) => setPreview((prev) => {
    const hr = prev?.hyphenReview;
    if (!hr) return prev;
    const cleared = new Set(decisions.filter((d) => d.verdict === 'clear').map((d) => d.token));
    const settled = new Map(decisions.filter((d) => d.verdict !== 'clear').map((d) => [d.token, d.verdict]));

    const items = hr.items
      .filter((i) => !settled.has(i.token))
      .concat(hr.decided
        .filter((d) => cleared.has(d.token))
        .map((d) => ({ ...d, reason: 'reopened — the model will judge it next import', verdict: null, confidence: null })));

    const decided = hr.decided
      .filter((d) => !cleared.has(d.token))
      .concat(hr.items
        .filter((i) => settled.has(i.token))
        .map((i) => ({ token: i.token, count: i.count, verdict: settled.get(i.token), wouldBecome: i.wouldBecome, decidedAt: null })));

    // Adjust `needingReview` by the DELTA, never by items.length. The rendered list can be a
    // capped subset of the real total — 60 rows of 78 — so assigning items.length quietly
    // rewrote the header from 78 to 60 on the first save or undo, and every count after that
    // described the screen rather than the book.
    const settledCount = hr.items.filter((i) => settled.has(i.token)).length;
    const reopenedCount = hr.decided.filter((d) => cleared.has(d.token)).length;
    const needingReview = Math.max(0, (hr.counts.needingReview ?? hr.items.length) - settledCount + reopenedCount);

    return {
      ...prev,
      hyphenReview: {
        ...hr,
        items,
        decided,
        counts: { ...hr.counts, needingReview, shown: items.length, operatorDecided: decided.length },
      },
    };
  });

  const putHyphenDecisions = async (decisions, note) => {
    if (!decisions.length) return;
    setSavingHyphens(true); setHyphenMsg(null); setError(null);
    try {
      const r = await apiClient.put(`/litgroups/${litGroupUuid}/books/${bookUuid}/hyphen-verdicts`, { decisions });
      setHyphenMsg(`${note} — ${r.data.operatorConfirmed} confirmed in total. Re-ingest to apply.`);
      setHyphenChoices({});
      applyHyphenResultLocally(decisions);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSavingHyphens(false);
    }
  };

  const saveHyphenDecisions = () => putHyphenDecisions(
    Object.entries(hyphenChoices).map(([token, verdict]) => ({ token, verdict })),
    `${Object.keys(hyphenChoices).length} decision(s) saved`
  );

  // Undo — drop the verdict entirely so the model judges the word again next import.
  const undoHyphenDecision = (token) => putHyphenDecisions(
    [{ token, verdict: 'clear' }], `"${token}" reopened`
  );

  // ---------------------------------------------------------------------
  // TEXT REPAIRS — spacing damage the extractor could SEE but would not guess at.
  //
  // "A NO T H E R TA NG E N T I A L" is ANOTHER TANGENTIAL, and a reader knows that instantly
  // while the machine cannot: kerning grouped the letters inside one run, so the word break has
  // no marker left in the text. Collapsing it anyway produced "ANOTHERTANGENTIAL" — a new defect
  // invented by the repair — so the extractor stops and asks.
  //
  // FREE TEXT, and ignoring is the default. Nothing here has to be answered; leave a span alone
  // and the book imports exactly as before, and simply sounds odd in that one place.
  // ---------------------------------------------------------------------
  const textReview = preview?.textReview || null;
  const [repairDrafts, setRepairDrafts] = useState({});     // damaged -> replacement
  const [savingRepairs, setSavingRepairs] = useState(false);
  const [repairMsg, setRepairMsg] = useState(null);

  const editRepair = (damaged, value) => setRepairDrafts((prev) => ({ ...prev, [damaged]: value }));

  const saveTextRepairs = async () => {
    // Everything already saved, plus everything edited now. Sent WHOLE — the server stores the
    // list as given, because nothing in the pipeline generates one of these and so there is no
    // machine opinion an operator decision needs protecting from.
    const existing = (preview?.savedRepairs || []).filter((r) => !(r.damaged in repairDrafts));
    const edited = Object.entries(repairDrafts)
      .filter(([, replacement]) => typeof replacement === 'string' && replacement.trim().length)
      .map(([damaged, replacement]) => ({ damaged, replacement: replacement.trim() }));
    const repairs = existing.concat(edited);
    if (!edited.length) { setRepairMsg('Nothing edited yet.'); return; }

    setSavingRepairs(true); setRepairMsg(null); setError(null);
    try {
      await apiClient.put(`/litgroups/${litGroupUuid}/books/${bookUuid}/text-repairs`, { repairs });
      setRepairMsg(`${edited.length} correction(s) saved — re-ingest to apply.`);
      setRepairDrafts({});
      // Move the corrected rows out of the work list locally. Re-running the preview would be
      // the obvious refresh and is the wrong one: it re-reads the whole PDF.
      setPreview((prev) => (prev ? {
        ...prev,
        savedRepairs: repairs,
        textReview: prev.textReview ? {
          ...prev.textReview,
          items: prev.textReview.items.filter((i) => !edited.some((e) => e.damaged === i.damaged)),
          decided: (prev.textReview.decided || []).concat(
            prev.textReview.items
              .filter((i) => edited.some((e) => e.damaged === i.damaged))
              .map((i) => ({ ...i, replacement: edited.find((e) => e.damaged === i.damaged).replacement }))
          ),
        } : prev.textReview,
      } : prev));
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSavingRepairs(false);
    }
  };

  const t = preview?.summary?.trim || {};
  // Preview's version confirms which engine ran THIS preview; the casting-payload version confirms the
  // running server. If they differ, the server module is stale (failed/partial restart).
  const previewEngine = preview?.engineVersion || null;

  return (
    <Segment>
      <Header as="h3">
        <Icon name="object group outline" />
        <Header.Content>
          Ingest Lab — Layout Preview &amp; Confirm
          <Header.Subheader>See how we read this book, correct any misreads, and confirm before it enters the pipeline</Header.Subheader>
        </Header.Content>
      </Header>
      {(engineVersion || previewEngine) && (
        <div style={{ fontSize: '0.78em', color: '#999', marginTop: '-0.5em', marginBottom: '0.4em' }}>
          <Icon name="microchip" /> engine <code style={{ color: '#666' }}>{engineVersion || '—'}</code>
          {previewEngine && previewEngine !== engineVersion && (
            <span style={{ color: '#db2828' }}> · this preview ran <code>{previewEngine}</code> (server module differs — restart)</span>
          )}
          {previewEngine && previewEngine === engineVersion && <span style={{ color: '#21ba45' }}> · preview matches ✓</span>}
        </div>
      )}
      {preview && (
        <div style={{ fontSize: '0.78em', color: '#777', marginBottom: '0.75em' }}>
          <Icon name="file pdf outline" /> converted <code style={{ color: '#555' }}>{preview.pdfFileName || '(unknown)'}</code>
          {' '}· {preview.pages ? preview.pages.length : 0} pages{preview.pageRange ? ` (range ${preview.pageRange})` : ''}
          {' '}· from S3 — confirm this is the edition you expect
        </div>
      )}

      {withPdf.length === 0 ? (
        <Message info><Icon name="info circle" />No book in this group has a source PDF on record to preview.</Message>
      ) : (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75em', alignItems: 'center' }}>
            <Dropdown placeholder="Select a book" selection search options={bookOptions} value={bookUuid}
              onChange={(e, { value }) => { setBookUuid(value); setPreview(null); setError(null); }} style={{ minWidth: 260 }} />
            <Input label="Pages" placeholder="e.g. 15-30 (optional)" value={pageRange}
              onChange={(e) => setPageRange(e.target.value)} style={{ width: 220 }} />
            {/* No confirm. It was a COST gate, not a safety one — the preview is read-only
                and imports nothing — and the conversion is now cached on the source PDF's
                content hash, so a given manuscript converts once and every later preview is
                a free cache hit. Guarding a free, read-only action behind a modal is pure
                friction. The first conversion of a new PDF still bills, which the note
                beside the button says. */}
            <Button primary disabled={!bookUuid || loading} onClick={runPreview}><Icon name="eye" /> Preview layout</Button>
            <span style={{ color: '#999', fontSize: '0.85em' }}>
              <Icon name="dollar sign" />First conversion of a PDF is billed; repeats are cached and free. A page range keeps the first one cheap.
            </span>
          </div>
        </>
      )}

      {loading && (
        <Segment style={{ marginTop: '1em' }}>
          <Loader active inline size="small" /> <span style={{ marginLeft: '0.5em' }}>Converting &amp; classifying… a full book can take up to a minute (a page range is much faster).</span>
        </Segment>
      )}
      {error && <Message negative style={{ marginTop: '1em' }} onDismiss={() => setError(null)}><Icon name="warning sign" />{error}</Message>}

      {preview && !loading && (
        <div style={{ marginTop: '1em' }}>
          <ProfileCard profile={preview.profile} />

          {/* Live summary from the BC's current corrections. */}
          <Segment>
            <Statistic.Group size="mini" widths="five">
              <Statistic><Statistic.Value>{derived.bodyParas}</Statistic.Value><Statistic.Label>body paras</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{derived.bodyChars.toLocaleString()}</Statistic.Value><Statistic.Label>body chars</Statistic.Label></Statistic>
              <Statistic color={derived.notes.length ? undefined : 'grey'}><Statistic.Value>{derived.notes.length}</Statistic.Value><Statistic.Label>note blobs</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{derived.scene}</Statistic.Value><Statistic.Label>scene breaks</Statistic.Label></Statistic>
              <Statistic><Statistic.Value>{derived.chrome + derived.trimmed}</Statistic.Value><Statistic.Label>dropped</Statistic.Label></Statistic>
            </Statistic.Group>
            <Message size="tiny" style={{ marginTop: '1em' }}>
              <Icon name="cut" /><strong>Auto-trim ({t.method || '?'}):</strong> {t.front || 0} front / {t.rear || 0} rear dropped.
              {t.bodyStartText && <> Body starts: <em>“{t.bodyStartText.slice(0, 90)}{t.bodyStartText.length > 90 ? '…' : ''}”</em></>}
              {(overrides.bodyStartIdx != null || overrides.bodyEndIdx != null) &&
                <span style={{ color: '#b58105' }}> · BC boundary override active</span>}
            </Message>
          </Segment>

          {/* Save / Revert / Reset bar. */}
          <Segment clearing style={{ background: dirty ? '#fffaf0' : undefined }}>
            <Button.Group floated="right">
              <Button disabled={!dirty || saving} onClick={revertToSaved} title="Undo unsaved edits — back to the last saved confirmation">Revert</Button>
              <Button.Or />
              <Button disabled={!hasOverrides || saving} onClick={resetOverrides} title="Clear all corrections back to the original classifier read">Reset all</Button>
              <Button.Or />
              <Button primary loading={saving} disabled={!dirty || saving} onClick={saveOverrides}>
                <Icon name="check" /> Save confirmation
              </Button>
            </Button.Group>
            <span style={{ lineHeight: '2.6em', color: dirty ? '#b58105' : '#767676' }}>
              {dirty ? <><Icon name="pencil" />Unsaved corrections</> : <><Icon name="check circle" />{savedOverrides ? 'Confirmed — saved corrections applied' : 'No corrections — classifier read as-is'}</>}
            </span>
            {saveMsg && <Message positive size="tiny" style={{ marginTop: '0.5em' }} onDismiss={() => setSaveMsg(null)}>{saveMsg}</Message>}
          </Segment>

          {/* Hyphen review — the tokens the rule could not prove, and the model may have got
              wrong. Sits above Re-ingest deliberately: decide first, then apply. */}
          {/* Render while there is EITHER work to do OR decisions to revisit. Gating on
              `items` alone meant deciding the last one unmounted the whole segment and took
              every undo with it — the same unreachable-decision bug the `decided` list was
              added to fix, one level up. */}
          {/* Spacing damage the extractor refused to guess at. Free text, and ignoring is the
              default — say so plainly, or an empty box reads as an outstanding task.

              ALWAYS SHOWN once a preview exists, including when there is nothing to do. A section
              that only appears when it has work cannot tell you the difference between "this book
              is clean" and "this check did not run" — and that is exactly the confusion that hid
              the block entirely when the preview was looking in the wrong place. */}
          {textReview && (
            <Segment>
              <div style={{ fontSize: '0.9em', color: '#555', marginBottom: '0.6em' }}>
                <Icon name="i cursor" /> <strong>Text the reader can fix and the machine cannot</strong> —{' '}
                {textReview.items.length > 0 ? (
                  <>
                    {textReview.items.length} item(s). Type the text you want spoken; leave one alone and
                    the book imports as it is, sounding odd just there. One correction fixes every
                    occurrence in the book.
                  </>
                ) : (
                  <>nothing outstanding.</>
                )}
                {textReview.decided?.length > 0 && <> {textReview.decided.length} already corrected by you.</>}
              </div>

              {textReview.items.length === 0 && (
                <div style={{ fontSize: '0.9em', color: '#2c662d' }}>
                  <Icon name="check circle" color="green" />
                  {textReview.decided?.length > 0
                    ? ' Every span you were offered has been corrected.'
                    : ' The extractor recovered every word on its own — nothing to do here.'}
                </div>
              )}

              {textReview.items.map((i) => (
                <div key={i.damaged} style={{ marginBottom: '0.7em' }}>
                  {/* Colour carries the same distinction the label spells out. Red is for text
                      that is BROKEN; an abbreviation is intact and merely ambiguous, so it gets
                      violet — attention without blame, and deliberately not green, which would
                      read as "already handled". */}
                  <div style={{
                    fontFamily: 'monospace',
                    fontSize: '0.88em',
                    color: i.kind === 'abbreviation' ? '#9435b9' : '#912d2b',
                  }}>
                    {i.damaged}
                    {i.count > 1 && <span style={{ color: '#888' }}> &nbsp;×{i.count}</span>}
                    {/* An abbreviation is NOT damaged text — it is right, and will be read wrong.
                        Saying which is which stops the block claiming the book has a defect it
                        does not have. */}
                    {i.kind === 'abbreviation' && (
                      <span style={{ color: '#888', fontFamily: 'inherit' }}> &nbsp;— spelled correctly, but a synthetic reader has to guess</span>
                    )}
                  </div>
                  {i.sample && (
                    <div style={{ fontSize: '0.8em', color: '#888', margin: '0.15em 0 0.3em 0' }}>…{i.sample}…</div>
                  )}
                  {/* Alternatives fill the box rather than committing anything, so the choice is
                      still the operator's and still editable afterwards. */}
                  {i.alternatives?.length > 1 && (
                    <div style={{ margin: '0.2em 0 0.3em 0' }}>
                      {i.alternatives.map((alt) => (
                        <Button
                          key={alt}
                          size="mini"
                          basic
                          compact
                          type="button"
                          onClick={() => editRepair(i.damaged, alt)}
                          style={{ marginRight: '0.35em' }}
                        >
                          {alt}
                        </Button>
                      ))}
                    </div>
                  )}
                  <Input
                    fluid
                    size="small"
                    placeholder={i.suggestion}
                    value={repairDrafts[i.damaged] ?? ''}
                    onChange={(e, d) => editRepair(i.damaged, d.value)}
                  />
                </div>
              ))}

              {textReview.items.length > 0 && (
                <Button
                  size="small"
                  primary
                  loading={savingRepairs}
                  disabled={savingRepairs}
                  onClick={saveTextRepairs}
                  style={{ marginTop: '0.4em' }}
                >
                  Save corrections
                </Button>
              )}
              {repairMsg && <span style={{ marginLeft: '0.8em', fontSize: '0.85em', color: '#2c662d' }}>{repairMsg}</span>}

              {textReview.decided?.length > 0 && (
                <div style={{ marginTop: '0.8em', fontSize: '0.85em', color: '#555' }}>
                  {textReview.decided.map((d) => (
                    <div key={d.damaged} style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                      <Icon name="check" color="green" /> {d.damaged} &nbsp;→&nbsp; <strong>{d.replacement}</strong>
                    </div>
                  ))}
                </div>
              )}
            </Segment>
          )}

          {hyphenReview && (hyphenReview.items.length > 0 || hyphenReview.decided?.length > 0) && (
            <Segment>
              <div style={{ fontSize: '0.9em', color: '#555', marginBottom: '0.6em' }}>
                {hyphenReview.counts.adjudicated ? (
                  <>
                    <Icon name="text width" /> <strong>Hyphens worth a look</strong> — {hyphenReview.counts.needingReview} of{' '}
                    {hyphenReview.counts.inBody} suspects in the body
                    {/* Say when the list is truncated. A capped list that does not admit it
                        reads as complete, and "I worked through them all" then means 60 of
                        78 — the same silent-truncation shape as the MAX_SUSPECTS cap. */}
                    {hyphenReview.counts.needingReview > hyphenReview.counts.shown && (
                      <>, <strong>showing the top {hyphenReview.counts.shown}</strong> — decide these and re-preview for the rest</>
                    )}.{' '}
                    {hyphenReview.counts.trimmedAway > 0 && <>({hyphenReview.counts.trimmedAway} more sit in front/back matter and never get spoken.){' '}</>}
                    {hyphenReview.counts.operatorDecided > 0 && <>{hyphenReview.counts.operatorDecided} already confirmed by you.{' '}</>}
                  </>
                ) : (
                  // Nothing has judged this book yet, so the list below is simply the rule's
                  // unproven suspects — not a curated set of doubtful calls. Say so, or the
                  // volume reads as a defect and deciding 60 by hand looks like the job.
                  <>
                    <Icon name="text width" /> <strong>Hyphens the rule could not prove</strong> — {hyphenReview.counts.inBody} in the body,
                    showing the {hyphenReview.counts.shown} most suspicious.{' '}
                    {hyphenReview.counts.trimmedAway > 0 && <>({hyphenReview.counts.trimmedAway} more are front/back matter and never get spoken.){' '}</>}
                    <br />
                    <span style={{ color: '#b58105' }}>
                      <Icon name="info circle" /> This book has no model verdicts yet — import or re-ingest first and
                      the model settles most of these. Come back and this list becomes just the ones it was unsure about.
                      Anything you decide here is permanent either way.
                    </span>
                  </>
                )}
                <br />
                <span style={{ fontSize: '0.92em', color: '#767676' }}>
                  <strong>Join</strong> if a line break split one word — <em>per-ception → perception</em>.{' '}
                  <strong>Keep</strong> if the author wrote it — a name, a coinage, a compound.{' '}
                  When in doubt, keep: a missed join is one audible hyphen, a wrong join corrupts the word.
                </span>
              </div>

              {hyphenReview.items.length === 0 && (
                <div style={{ fontSize: '0.85em', color: '#2c662d', padding: '0.3em 0' }}>
                  <Icon name="check circle" /> Nothing left to review — every doubtful hyphen in the body has been settled.
                </div>
              )}

              <List divided size="small" style={{ maxHeight: 300, overflowY: 'auto', margin: 0 }}>
                {hyphenReview.items.map((h) => {
                  const choice = hyphenChoices[h.token];
                  return (
                    <List.Item key={h.token} style={{ padding: '6px 0' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <Button.Group size="mini" style={{ flex: '0 0 auto' }}>
                          <Button positive={choice === 'join'} basic={choice !== 'join'}
                            onClick={() => chooseHyphen(h.token, 'join')} title={`Join → ${h.wouldBecome}`}>Join</Button>
                          <Button positive={choice === 'keep'} basic={choice !== 'keep'}
                            onClick={() => chooseHyphen(h.token, 'keep')} title="Keep the hyphen — the author wrote it">Keep</Button>
                        </Button.Group>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: '0.95em' }}>
                            <code>{h.token}</code>
                            <span style={{ color: '#999' }}> → {h.wouldBecome}</span>
                            {h.count > 1 && <span style={{ color: '#999', fontSize: '0.85em' }}> · {h.count}×</span>}
                            {/* `size` is inert on Semantic Labels in this build — style it. */}
                            <Label style={{ fontSize: 9, padding: '2px 5px', marginLeft: 6, background: '#f3f4f5', color: '#767676' }}>
                              {h.reason}{h.confidence != null && ` · ${h.verdict} ${h.confidence.toFixed(2)}`}
                            </Label>
                          </div>
                          {h.context && (
                            <div style={{ color: '#8a8a8a', fontSize: '0.82em', fontStyle: 'italic', marginTop: 2 }}>
                              {h.context}
                            </div>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  );
                })}
              </List>

              <div style={{ marginTop: '0.6em', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Button primary size="small" loading={savingHyphens}
                  disabled={!Object.keys(hyphenChoices).length || savingHyphens} onClick={saveHyphenDecisions}>
                  <Icon name="check" /> Save {Object.keys(hyphenChoices).length || ''} decision{Object.keys(hyphenChoices).length === 1 ? '' : 's'}
                </Button>
                <span style={{ fontSize: '0.82em', color: '#767676' }}>
                  Saved decisions are permanent — the model is never asked about that word again, and re-ingest applies them.
                </span>
              </div>
              {hyphenMsg && <Message positive size="tiny" style={{ marginTop: '0.5em' }} onDismiss={() => setHyphenMsg(null)}>{hyphenMsg}</Message>}

              {/* Decisions already made. Shown so they can be UNDONE — a settled token drops
                  out of the work list above, so without this a mis-click would be permanent
                  and unreachable. Undo removes the verdict entirely, and the model judges the
                  word fresh on the next import. */}
              {hyphenReview.decided?.length > 0 && (
                <div style={{ marginTop: '0.8em', borderTop: '1px solid #eee', paddingTop: '0.6em' }}>
                  <div style={{ fontSize: '0.82em', color: '#767676', marginBottom: '0.4em' }}>
                    <Icon name="check circle" color="green" /> {hyphenReview.decided.length} decided by you — the model is not asked about these.
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {hyphenReview.decided.map((d) => (
                      <Label key={d.token} style={{ fontSize: 11, padding: '4px 6px', background: '#f0f7f0', border: '1px solid #cfe3cf' }}>
                        <code>{d.token}</code>
                        <span style={{ color: '#767676' }}> · {d.verdict === 'join' ? `→ ${d.wouldBecome}` : 'kept'}</span>
                        <Icon name="undo" link title="Undo — let the model judge this word again next import"
                          style={{ marginLeft: 6, opacity: 0.7 }}
                          onClick={() => undoHyphenDecision(d.token)} />
                      </Label>
                    ))}
                  </div>
                </div>
              )}
            </Segment>
          )}

          {/* Replace the source PDF — the bytes the ingest reads. Sits ABOVE Re-ingest
              deliberately: swap the manuscript first, then apply it. Two acts, never one. */}
          <Segment>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5em' }}>
              <span style={{ fontSize: '0.9em', color: '#555' }}>
                <Icon name="file pdf outline" /> Replace the <strong>source PDF</strong> this book is ingested from — after editing the manuscript and re-exporting.
              </span>
              <Button basic color="teal" disabled={!bookUuid || !srcPdf || srcBusy || !srcFile}
                loading={srcBusy} onClick={() => setSrcConfirm(true)}>
                <Icon name="upload" /> Replace source PDF
              </Button>
            </div>

            {srcPdf && (
              <div style={{ fontSize: '0.82em', color: '#555', marginTop: '0.6em', lineHeight: 1.7 }}>
                <div>
                  <strong>Stored now:</strong> <code>{srcPdf.fileName}</code>
                  {srcPdf.current
                    ? ` · ${srcPdf.current.size.toLocaleString()} bytes · ${new Date(srcPdf.current.lastModified).toLocaleString()}`
                    : ' · nothing stored at this key yet'}
                </div>
                {/* The name is not editable, and that is the feature. */}
                <div style={{ color: '#888' }}>
                  The replacement is stored under this exact name — it is what the re-ingest asks for.
                  Your local filename does not matter.
                </div>
                <input ref={srcInputRef} type="file" accept="application/pdf,.pdf"
                  style={{ marginTop: '0.5em' }} disabled={srcBusy}
                  onChange={(e) => { setSrcFile(e.target.files?.[0] || null); setSrcMsg(null); }} />
                {srcFile && (
                  <div style={{ marginTop: '0.35em' }}>
                    <Icon name="arrow right" /> <strong>{srcFile.name}</strong> · {srcFile.size.toLocaleString()} bytes
                    {srcPdf.current && srcFile.size === srcPdf.current.size && (
                      <span style={{ color: '#b58105' }}> — same size as the stored file; is this a new export?</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {srcMsg && <Message positive size="tiny" style={{ marginTop: '0.5em' }} onDismiss={() => setSrcMsg(null)}>{srcMsg}</Message>}

            <Confirm open={srcConfirm} header="Replace this book's source PDF?"
              content={
                srcPdf && srcFile
                  ? `"${srcPdf.bookName}" will be ingested from ${srcFile.name} (${srcFile.size.toLocaleString()} bytes) `
                    + `instead of the ${srcPdf.current ? `${srcPdf.current.size.toLocaleString()}-byte` : 'currently stored'} file. `
                    + 'The current version is kept as a dated copy. Nothing is re-ingested and no audio changes '
                    + 'until you run Re-ingest — this only swaps the bytes.'
                  : ''
              }
              confirmButton="Replace" onCancel={() => setSrcConfirm(false)} onConfirm={runReplaceSourcePdf} />
          </Segment>

          {/* Re-ingest — push the SAVED corrections into the actual imported book. */}
          <Segment>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5em' }}>
              <span style={{ fontSize: '0.9em', color: '#555' }}>
                <Icon name="sync" /> Apply your <strong>saved</strong> corrections to the imported book — re-runs the ingest and rebuilds its script + dialogue lines.
              </span>
              <Button color="teal" disabled={!bookUuid || reingesting || dirty} loading={reingesting} onClick={() => setReingestConfirm(true)}>
                <Icon name="redo" /> Re-ingest book
              </Button>
            </div>
            {dirty && <div style={{ fontSize: '0.8em', color: '#b58105', marginTop: '0.4em' }}>Save your corrections first — re-ingest applies the saved overrides, not unsaved edits.</div>}
            {reingestMsg && <Message positive size="tiny" style={{ marginTop: '0.5em' }} onDismiss={() => setReingestMsg(null)}>{reingestMsg}</Message>}
            <Confirm open={reingestConfirm} header="Re-ingest this book?"
              content="Re-runs the layout ingest with your saved corrections and rebuilds the book's script + dialogue lines. Billed (PDF→HTML + attribution) and runs in the background — a few minutes. Re-derive casting afterwards to refresh tiers."
              confirmButton="Re-ingest" onCancel={() => setReingestConfirm(false)} onConfirm={runReingest} />
          </Segment>

          <Grid stackable style={{ marginTop: 0 }}>
            <Grid.Column width={7}>
              <Segment>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75em' }}>
                  <Header as="h5" style={{ margin: 0 }}>Annotated page</Header>
                  <Button.Group size="mini">
                    <Button icon disabled={pageIdx <= 0} onClick={() => setPageIdx((i) => Math.max(0, i - 1))}><Icon name="chevron left" /></Button>
                    <Button.Or text={`${page ? page.page : '–'} · ${pageIdx + 1}/${pages.length}`} />
                    <Button icon disabled={pageIdx >= pages.length - 1} onClick={() => setPageIdx((i) => Math.min(pages.length - 1, i + 1))}><Icon name="chevron right" /></Button>
                  </Button.Group>
                </div>
                <PageCanvas page={page} effectiveKind={effectiveKind} selectedIdx={selectedIdx} onSelect={setSelectedIdx} />
                <p style={{ fontSize: '0.82em', color: '#999', textAlign: 'center', marginTop: '0.5em' }}>Click a box to re-tag it or set the body boundary.</p>
              </Segment>
            </Grid.Column>
            <Grid.Column width={9}>
              {selectedEl ? (
                <Segment color="blue">
                  <Header as="h5" style={{ marginBottom: '0.4em' }}>
                    Selected element
                    <Label size="tiny" horizontal style={{ marginLeft: '0.6em', background: `${kindStyle(effectiveKind(selectedEl)).color}22`, color: kindStyle(effectiveKind(selectedEl)).color }}>
                      {kindStyle(effectiveKind(selectedEl)).label}
                    </Label>
                    <span style={{ float: 'right', fontSize: '0.8em', color: '#aaa' }}>p{selectedEl.page} · #{selectedEl.idx}</span>
                  </Header>
                  <div style={{ fontSize: '0.88em', color: '#444', maxHeight: 90, overflow: 'auto', marginBottom: '0.6em', background: '#f9f9f9', padding: '0.4em 0.6em', borderRadius: 3 }}>
                    {selectedEl.text || <em>(empty)</em>}
                  </div>
                  <div style={{ marginBottom: '0.5em' }}>
                    <span style={{ fontSize: '0.8em', color: '#888', marginRight: '0.5em' }}>Re-tag as:</span>
                    {RETAG.map((r) => (
                      <Button key={r.kind} size="mini" basic={effectiveKind(selectedEl) !== (r.kind === 'drop' ? 'trimmed' : r.kind)}
                        color={r.kind === 'drop' ? 'red' : undefined}
                        onClick={() => setKind(selectedEl.idx, r.kind)} style={{ marginBottom: 4 }}>{r.label}</Button>
                    ))}
                  </div>
                  <div>
                    <span style={{ fontSize: '0.8em', color: '#888', marginRight: '0.5em' }}>Body boundary:</span>
                    <Button size="mini" toggle active={overrides.bodyStartIdx === selectedEl.idx}
                      onClick={() => setBoundary('bodyStartIdx', selectedEl.idx)}>Start body here</Button>
                    <Button size="mini" toggle active={overrides.bodyEndIdx === selectedEl.idx}
                      onClick={() => setBoundary('bodyEndIdx', selectedEl.idx)}>End body here</Button>
                  </div>
                  <Button size="mini" basic floated="right" style={{ marginTop: '0.4em' }} onClick={() => setSelectedIdx(null)}>Done</Button>
                </Segment>
              ) : (
                <Segment>
                  <Header as="h5" style={{ marginBottom: 0 }}>How we classified this book</Header>
                  <Legend kindsPresent={kindsPresent} />
                  <p style={{ fontSize: '0.88em', color: '#666' }}>
                    Saturated boxes are <strong>kept</strong> content; gray is dropped chrome; dashed red is
                    <strong> trimmed</strong> front/rear matter. <strong>Click any box</strong> to re-tag it (e.g. a false
                    sidenote → Body, or Drop) or to set the body start/end. Corrections apply live here and, once you
                    <strong> Save confirmation</strong>, the import reproduces exactly this read.
                  </p>
                </Segment>
              )}

              {/* Note blobs from the current (corrected) read, with one-click fixes. Grouped to match the
                  real note count; click a badge/page chip to jump the annotated page to that note. */}
              {derived.notes.length > 0 && (
                <Segment>
                  <Header as="h5"><Icon name="sticky note outline" /> Note blobs ({derived.notes.length})
                    <span style={{ fontWeight: 400, fontSize: '0.72em', color: '#999', marginLeft: '0.6em' }}>click a badge to jump to its page</span>
                  </Header>
                  <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                    <List divided relaxed size="small">
                      {derived.notes.slice(0, 120).map((n) => {
                        const s = kindStyle(n.noteType);
                        const selectedHere = n.idxs.includes(selectedIdx);
                        return (
                          <List.Item key={n.idx} style={selectedHere ? { background: '#eef6ff' } : undefined}>
                            <List.Content floated="right">
                              <Button size="mini" basic compact onClick={() => setKinds(n.idxs, 'body')}>→ Body</Button>
                              <Button size="mini" basic compact color="red" onClick={() => setKinds(n.idxs, 'drop')}>Drop</Button>
                            </List.Content>
                            <Label size="tiny" as="a" onClick={() => goToNotePage(n)} title={`Jump to page ${n.page}`}
                              style={{ background: `${s.color}22`, color: s.color, border: `1px solid ${s.color}` }}>{s.label}</Label>
                            <span onClick={() => goToNotePage(n)} title={`Jump to page ${n.page}`}
                              style={{ fontSize: '0.8em', color: '#2185d0', margin: '0 0.4em', cursor: 'pointer' }}>p{n.page} →</span>
                            {n.idxs.length > 1 && <span style={{ fontSize: '0.72em', color: '#bbb' }}>({n.idxs.length} lines) </span>}
                            <span style={{ fontSize: '0.88em', color: '#444' }}>{(n.text || '').slice(0, 120)}{(n.text || '').length > 120 ? '…' : ''}</span>
                          </List.Item>
                        );
                      })}
                      {derived.notes.length > 120 && <List.Item><em style={{ color: '#999' }}>+{derived.notes.length - 120} more…</em></List.Item>}
                    </List>
                  </div>
                </Segment>
              )}
            </Grid.Column>
          </Grid>
        </div>
      )}
    </Segment>
  );
};

export default IngestPreviewPanel;
